/**
 * Admin - Gestion Subscriptions Marketing Studio
 * ✅ Header fixe, tableau scrollable, pagination 50
 * ✅ Badge PRO/FREE + bouton toggle pricing_type
 */

import React, { useState, useEffect } from 'react';
import {
  Users, Search, Edit2, Save, X, AlertCircle,
  CheckCircle, Plus, Trash2, Sparkles, Clock,
  RefreshCw, Package, Image as ImageIcon, Video,
  Crown, Gift
} from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../../config';

const PAGE_SIZE = 50;

export default function MarketingStudioAdmin() {
  const [users,       setUsers]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [searchTerm,  setSearchTerm]  = useState('');
  const [page,        setPage]        = useState(1);
  const [editingUser, setEditingUser] = useState(null);
  const [creatingFor, setCreatingFor] = useState(null);
  const [message,     setMessage]     = useState(null);
  const [stats,       setStats]       = useState({
    total_users: 0, users_with_subscription: 0,
    total_images_used: 0, total_videos_used: 0
  });
  const [formData, setFormData] = useState({
    max_images: 50, max_videos: 25, plan_type: 'monthly', duration_days: 30
  });

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/marketing-studio/users`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (r.data.success) { setUsers(r.data.users); setStats(r.data.stats); }
    } catch { showMessage('Erreur chargement', 'error'); }
    finally { setLoading(false); }
  };

  const createSubscription = async (userId) => {
    try {
      const r = await axios.post(`${API_URL}/api/admin/marketing-studio/create-subscription`,
        { user_id: userId, ...formData },
        { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } }
      );
      if (r.data.success) { showMessage('Subscription PRO créée ✅', 'success'); loadUsers(); setCreatingFor(null); }
    } catch (e) { showMessage(e.response?.data?.error || 'Erreur création', 'error'); }
  };

  const updateQuotas = async (subscriptionId) => {
    try {
      const r = await axios.post(`${API_URL}/api/admin/marketing-studio/update-quotas`,
        { subscription_id: subscriptionId, max_images: formData.max_images, max_videos: formData.max_videos },
        { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } }
      );
      if (r.data.success) { showMessage('Quotas mis à jour ✅', 'success'); loadUsers(); setEditingUser(null); }
    } catch { showMessage('Erreur mise à jour', 'error'); }
  };

  const togglePricingType = async (subscriptionId, currentType) => {
    const newType = currentType === 'pro' ? 'free' : 'pro';
    if (!window.confirm(`Passer en ${newType.toUpperCase()} ?`)) return;
    try {
      const r = await axios.post(`${API_URL}/api/admin/marketing-studio/update-pricing-type`,
        { subscription_id: subscriptionId, pricing_type: newType },
        { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } }
      );
      if (r.data.success) { showMessage(`Passé en ${newType.toUpperCase()} ✅`, 'success'); loadUsers(); }
    } catch { showMessage('Erreur changement plan', 'error'); }
  };

  const resetUsage = async (subscriptionId, mediaType) => {
    if (!window.confirm(`Réinitialiser ${mediaType} ?`)) return;
    try {
      const r = await axios.post(`${API_URL}/api/admin/marketing-studio/reset-usage`,
        { subscription_id: subscriptionId, media_type: mediaType },
        { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } }
      );
      if (r.data.success) { showMessage('Réinitialisé ✅', 'success'); loadUsers(); }
    } catch { showMessage('Erreur reset', 'error'); }
  };

  const deleteSubscription = async (subscriptionId) => {
    if (!window.confirm('Supprimer cette subscription ?')) return;
    try {
      const r = await axios.delete(`${API_URL}/api/admin/marketing-studio/delete-subscription`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }, data: { subscription_id: subscriptionId } }
      );
      if (r.data.success) { showMessage('Supprimée ✅', 'success'); loadUsers(); }
    } catch { showMessage('Erreur suppression', 'error'); }
  };

  const startEdit   = (user) => { setEditingUser(user.user_id); setFormData({ max_images: user.max_images, max_videos: user.max_videos, plan_type: user.plan_type||'monthly', duration_days: 30 }); };
  const startCreate = (user) => { setCreatingFor(user.user_id); setFormData({ max_images: 50, max_videos: 25, plan_type: 'monthly', duration_days: 30 }); };
  const cancelEdit  = ()     => { setEditingUser(null); setCreatingFor(null); };

  const showMessage = (text, type) => { setMessage({ text, type }); setTimeout(() => setMessage(null), 4000); };

  const filtered   = users.filter(u => {
    const s = searchTerm.toLowerCase();
    return u.email?.toLowerCase().includes(s) || u.name?.toLowerCase().includes(s) || u.user_id?.toLowerCase().includes(s);
  });
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE) || 1;
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 64px)', overflow: 'hidden', padding: '24px 32px 0', background: 'linear-gradient(135deg,#f5f3ff,#fdf2f8,#fff7ed)' }}>

      {/* ══ HEADER FIXE ══ */}
      <div className="flex-shrink-0">

        {/* Titre + bouton */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Sparkles className="w-8 h-8 text-purple-600" />
              Marketing Studio — Admin
            </h1>
            <p className="text-gray-500 text-sm mt-1">Gestion subscriptions · PRO = sans watermark</p>
          </div>
          <button onClick={loadUsers} disabled={loading}
            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold disabled:opacity-50 shadow-lg">
            {loading ? <><Clock className="w-4 h-4 animate-spin" />Chargement…</> : <><RefreshCw className="w-4 h-4" />Actualiser</>}
          </button>
        </div>

        {/* Message */}
        {message && (
          <div className={`mb-3 p-3 rounded-xl flex items-center gap-2 text-sm font-medium ${
            message.type === 'success' ? 'bg-green-100 border border-green-300 text-green-800' : 'bg-red-100 border border-red-300 text-red-800'
          }`}>
            {message.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
            {message.text}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-4 gap-3 mb-4">
          {[
            { icon: Users,     label: 'Total Clients',       value: stats.total_users,              color: 'from-blue-500 to-cyan-500' },
            { icon: Package,   label: 'Avec Subscription',   value: stats.users_with_subscription,  color: 'from-green-500 to-emerald-500' },
            { icon: ImageIcon, label: 'Images Générées',     value: stats.total_images_used,        color: 'from-purple-500 to-pink-500' },
            { icon: Video,     label: 'Vidéos Générées',     value: stats.total_videos_used,        color: 'from-pink-500 to-orange-500' },
          ].map((k, i) => (
            <div key={i} className={`bg-gradient-to-br ${k.color} rounded-xl p-4 text-white flex items-center justify-between shadow-lg`}>
              <div><k.icon className="w-7 h-7 opacity-80 mb-1" /><div className="text-xs font-semibold opacity-80">{k.label}</div></div>
              <div className="text-3xl font-black">{k.value}</div>
            </div>
          ))}
        </div>

        {/* Légende + Search */}
        <div className="flex items-center gap-4 mb-3">
          <div className="flex items-center gap-3 text-xs">
            <span className="flex items-center gap-1 text-yellow-600 font-bold bg-yellow-50 px-2 py-1 rounded-lg border border-yellow-200"><Crown className="w-3.5 h-3.5" />PRO = pas de watermark</span>
            <span className="flex items-center gap-1 text-blue-600 font-bold bg-blue-50 px-2 py-1 rounded-lg border border-blue-200"><Gift className="w-3.5 h-3.5" />FREE = watermark</span>
          </div>
          <div className="relative flex-1 max-w-sm ml-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
              placeholder="Rechercher…"
              className="w-full pl-9 pr-4 py-2 border-2 border-gray-200 rounded-xl text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white" />
          </div>
        </div>
      </div>

      {/* ══ TABLEAU SCROLLABLE ══ */}
      <div className="flex-1 min-h-0 overflow-y-auto rounded-2xl border-2 border-purple-200 shadow-xl bg-white" style={{ scrollbarWidth: 'thin' }}>
        <table className="w-full" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
          <thead>
            <tr style={{ position: 'sticky', top: 0, zIndex: 10 }}>
              {['Client', 'Status', 'Plan', 'Images', 'Vidéos', 'Actions'].map((h, i) => (
                <th key={i} className={`px-5 py-3 text-xs font-black uppercase tracking-widest text-gray-600 border-b-2 border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50 ${i === 0 ? 'text-left' : 'text-center'} ${i === 5 ? 'text-right' : ''}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && users.length === 0 ? (
              <tr><td colSpan={6} className="py-20 text-center text-gray-400">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2 text-purple-500" /><p>Chargement…</p>
              </td></tr>
            ) : paginated.length === 0 ? (
              <tr><td colSpan={6} className="py-20 text-center text-gray-400">
                <Users className="w-10 h-10 mx-auto mb-2 opacity-30" /><p>Aucun client trouvé</p>
              </td></tr>
            ) : paginated.map(user => (
              <React.Fragment key={user.user_id}>
                <tr className="hover:bg-purple-50 transition">

                  {/* Client */}
                  <td className="px-5 py-3.5">
                    <div className="font-bold text-gray-900 text-sm">{user.name}</div>
                    <div className="text-gray-500 text-xs">{user.email}</div>
                    {user.has_subscription && (
                      <span className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                        <CheckCircle className="w-3 h-3" />{user.plan_type || 'Actif'}
                      </span>
                    )}
                  </td>

                  {/* Status */}
                  <td className="px-5 py-3.5 text-center">
                    {user.has_subscription
                      ? <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold"><CheckCircle className="w-3.5 h-3.5" />Active</span>
                      : <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-500 rounded-full text-xs font-semibold"><X className="w-3.5 h-3.5" />Aucune</span>}
                  </td>

                  {/* Plan */}
                  <td className="px-5 py-3.5 text-center">
                    {user.has_subscription ? (
                      <div className="flex flex-col items-center gap-1.5">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black border-2 ${
                          user.pricing_type === 'pro'
                            ? 'bg-yellow-50 text-yellow-700 border-yellow-300'
                            : 'bg-blue-50 text-blue-700 border-blue-200'
                        }`}>
                          {user.pricing_type === 'pro' ? <Crown className="w-3 h-3" /> : <Gift className="w-3 h-3" />}
                          {(user.pricing_type || 'free').toUpperCase()}
                        </span>
                        <button onClick={() => togglePricingType(user.subscription_id, user.pricing_type || 'free')}
                          className={`text-xs px-2 py-0.5 rounded-lg font-semibold transition border ${
                            user.pricing_type === 'pro'
                              ? 'bg-blue-50 hover:bg-blue-100 text-blue-600 border-blue-200'
                              : 'bg-yellow-50 hover:bg-yellow-100 text-yellow-700 border-yellow-300'
                          }`}>
                          {user.pricing_type === 'pro' ? '↓ Free' : '↑ PRO'}
                        </button>
                      </div>
                    ) : <span className="text-gray-300 text-xs">—</span>}
                  </td>

                  {/* Images */}
                  <td className="px-5 py-3.5">
                    {editingUser === user.user_id ? (
                      <div className="flex flex-col items-center gap-1">
                        <input type="number" value={formData.max_images}
                          onChange={e => setFormData({...formData, max_images: parseInt(e.target.value)||0})}
                          className="w-24 px-2 py-1.5 border-2 border-purple-300 rounded-lg text-center font-bold text-sm" />
                        <span className="text-xs text-gray-400">Utilisé: {user.used_images}</span>
                      </div>
                    ) : (
                      <div className="text-center">
                        <div className="text-gray-900 font-bold text-sm">{user.remaining_images} / {user.max_images}</div>
                        <div className="text-xs text-gray-400 mb-1">Utilisé: {user.used_images}</div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div className="bg-gradient-to-r from-purple-500 to-pink-500 h-1.5 rounded-full"
                            style={{ width: `${user.max_images > 0 ? (user.remaining_images/user.max_images)*100 : 0}%` }} />
                        </div>
                      </div>
                    )}
                  </td>

                  {/* Vidéos */}
                  <td className="px-5 py-3.5">
                    {editingUser === user.user_id ? (
                      <div className="flex flex-col items-center gap-1">
                        <input type="number" value={formData.max_videos}
                          onChange={e => setFormData({...formData, max_videos: parseInt(e.target.value)||0})}
                          className="w-24 px-2 py-1.5 border-2 border-pink-300 rounded-lg text-center font-bold text-sm" />
                        <span className="text-xs text-gray-400">Utilisé: {user.used_videos}</span>
                      </div>
                    ) : (
                      <div className="text-center">
                        <div className="text-gray-900 font-bold text-sm">{user.remaining_videos} / {user.max_videos}</div>
                        <div className="text-xs text-gray-400 mb-1">Utilisé: {user.used_videos}</div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div className="bg-gradient-to-r from-pink-500 to-orange-500 h-1.5 rounded-full"
                            style={{ width: `${user.max_videos > 0 ? (user.remaining_videos/user.max_videos)*100 : 0}%` }} />
                        </div>
                      </div>
                    )}
                  </td>

                  {/* Actions */}
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-1.5">
                      {editingUser === user.user_id ? (
                        <>
                          <button onClick={() => updateQuotas(user.subscription_id)} className="p-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg transition" title="Sauvegarder"><Save className="w-4 h-4" /></button>
                          <button onClick={cancelEdit} className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition" title="Annuler"><X className="w-4 h-4" /></button>
                        </>
                      ) : creatingFor === user.user_id ? (
                        <>
                          <button onClick={() => createSubscription(user.user_id)} className="p-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg transition" title="Créer"><CheckCircle className="w-4 h-4" /></button>
                          <button onClick={cancelEdit} className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition" title="Annuler"><X className="w-4 h-4" /></button>
                        </>
                      ) : user.has_subscription ? (
                        <>
                          <button onClick={() => startEdit(user)} className="p-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg transition" title="Modifier"><Edit2 className="w-4 h-4" /></button>
                          <button onClick={() => resetUsage(user.subscription_id, 'images')} className="p-2 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg transition" title="Reset images"><ImageIcon className="w-4 h-4" /></button>
                          <button onClick={() => resetUsage(user.subscription_id, 'videos')} className="p-2 bg-pink-100 hover:bg-pink-200 text-pink-700 rounded-lg transition" title="Reset vidéos"><Video className="w-4 h-4" /></button>
                          <button onClick={() => deleteSubscription(user.subscription_id)} className="p-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition" title="Supprimer"><Trash2 className="w-4 h-4" /></button>
                        </>
                      ) : (
                        <button onClick={() => startCreate(user)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:from-green-600 hover:to-emerald-600 transition text-xs font-bold shadow">
                          <Plus className="w-3.5 h-3.5" />Créer Sub PRO
                        </button>
                      )}
                    </div>
                  </td>
                </tr>

                {/* Formulaire création */}
                {creatingFor === user.user_id && (
                  <tr>
                    <td colSpan={6} className="px-5 py-3 bg-green-50 border-t border-green-200">
                      <div className="flex items-center gap-2 mb-2">
                        <Crown className="w-4 h-4 text-yellow-500" />
                        <span className="text-sm font-bold text-gray-700">Nouvelle subscription PRO (sans watermark)</span>
                      </div>
                      <div className="grid grid-cols-4 gap-3">
                        {[
                          { l: 'Images Max', k: 'max_images', border: 'border-purple-300' },
                          { l: 'Vidéos Max',  k: 'max_videos', border: 'border-pink-300' },
                        ].map(f => (
                          <div key={f.k}>
                            <label className="block text-xs font-medium text-gray-600 mb-1">{f.l}</label>
                            <input type="number" value={formData[f.k]}
                              onChange={e => setFormData({...formData, [f.k]: parseInt(e.target.value)||0})}
                              className={`w-full px-3 py-2 border-2 ${f.border} rounded-lg text-sm font-bold`} />
                          </div>
                        ))}
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Type Plan</label>
                          <select value={formData.plan_type} onChange={e => setFormData({...formData, plan_type: e.target.value})}
                            className="w-full px-3 py-2 border-2 border-green-300 rounded-lg text-sm">
                            <option value="monthly">Mensuel</option>
                            <option value="yearly">Annuel</option>
                            <option value="credits">Crédits</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Durée (jours)</label>
                          <input type="number" value={formData.duration_days}
                            onChange={e => setFormData({...formData, duration_days: parseInt(e.target.value)||30})}
                            className="w-full px-3 py-2 border-2 border-green-300 rounded-lg text-sm font-bold" />
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* ══ PAGINATION ══ */}
      <div className="flex-shrink-0 flex items-center justify-between py-3">
        <span className="text-gray-500 text-sm">{filtered.length} clients · page {page}/{totalPages}</span>
        {totalPages > 1 && (
          <div className="flex items-center gap-1.5">
            <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1}
              className="px-3 py-1.5 rounded-lg text-sm font-semibold disabled:opacity-30 bg-purple-100 hover:bg-purple-200 text-purple-700 transition">← Préc.</button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const p = totalPages <= 5 ? i+1 : page <= 3 ? i+1 : page >= totalPages-2 ? totalPages-4+i : page-2+i;
              return <button key={p} onClick={() => setPage(p)}
                className={`px-3 py-1.5 rounded-lg text-sm font-bold transition ${p === page ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}>{p}</button>;
            })}
            <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page >= totalPages}
              className="px-3 py-1.5 rounded-lg text-sm font-semibold disabled:opacity-30 bg-purple-100 hover:bg-purple-200 text-purple-700 transition">Suiv. →</button>
          </div>
        )}
      </div>
    </div>
  );
}